import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { SearchableDropdown, Button, SideBarModal, DatePicker } from '@/components';
import { showErrorToast, showSuccessToast } from '@/components/toast/globalToast';
import { IssueSeverity, Handover } from '@/types/common';
import { getListHandover } from '@/services/api/handovers';
import { v4 as uuidv4 } from 'uuid';
import { getBucketUrlImageV2, uploadFileImage, getBucketUrlFile, uploadTempFile } from '@/utils';

export type UploadedFileInfo = {
  file: File;
  url?: string;
  uploading?: boolean;
  error?: boolean;
};

export interface IncidentFormData {
  handoverID: string;
  vehiclePlatNomor: string;
  issueType: string;
  incidentDate: string;
  severity: IssueSeverity;
  chronology: string;
  costBearer: string;
  mitraName?: string;
  clientName?: string;
  vehicleOemName?: string;
  oemID?: string;
  driverID?: string;
  driverCode?: string;
  driverName?: string;
  clientID?: number;
  photos: UploadedFileInfo[];
  policeReports: UploadedFileInfo[];
  imageURL?: string;
  policeReportURL?: string;
  notes: string;
  selectedHandover?: Handover;
}

interface ReportIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: IncidentFormData) => Promise<void>;
  isLoading?: boolean;
}

const severityOptions = [
  { value: IssueSeverity.LOW, label: 'Low' },
  { value: IssueSeverity.MEDIUM, label: 'Medium' },
  { value: IssueSeverity.HIGH, label: 'High' },
  { value: IssueSeverity.CRITICAL, label: 'Critical' },
];

const INCIDENT_IMAGE_TYPE = 'incident';
const INCIDENT_FILE_TYPE = 'incident';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const FileIcon: React.FC<{ filename: string }> = ({ filename }) => {
  const ext = filename.split('.').pop()?.toUpperCase() || 'FILE';
  const isImage = ['JPG', 'JPEG', 'PNG', 'WEBP', 'GIF'].includes(ext);
  const isPdf = ext === 'PDF';
  const badgeColor = isPdf ? '#EF4444' : '#6B7280';

  return (
    <div className="w-10 h-10 flex-shrink-0">
      <svg viewBox="0 0 40 40" fill="none" className="w-full h-full">
        <path
          d="M10 4H22L30 12V34C30 35.1046 29.1046 36 28 36H10C8.89543 36 8 35.1046 8 34V6C8 4.89543 8.89543 4 10 4Z"
          fill="white"
          stroke="#D1D5DB"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M22 4V10C22 11.1046 22.8954 12 24 12H30"
          stroke="#D1D5DB"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="4" y="20" width="32" height="13" rx="3" fill={badgeColor} />
        <text
          x="20"
          y="29"
          textAnchor="middle"
          fill="white"
          fontSize="8"
          fontWeight="bold"
          fontFamily="sans-serif"
        >
          {isImage ? 'JPG' : isPdf ? 'PDF' : ext.slice(0, 4)}
        </text>
      </svg>
    </div>
  );
};

export const FileUploadField: React.FC<{
  label: React.ReactNode;
  required?: boolean;
  accept: string;
  files: UploadedFileInfo[];
  onFilesChange: (files: UploadedFileInfo[]) => void;
  uploadFn?: (file: File) => Promise<string>;
  className?: string;
}> = ({ label, required, accept, files, onFilesChange, uploadFn, className }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map((f) => ({
        file: f,
        uploading: !!uploadFn,
      }));
      let currentFiles = [...files, ...newFiles];
      onFilesChange(currentFiles);
      if (inputRef.current) inputRef.current.value = '';

      if (uploadFn) {
        for (let i = 0; i < newFiles.length; i++) {
          const fileInfo = newFiles[i];
          try {
            const uploadedUrl = await uploadFn(fileInfo.file);
            currentFiles = currentFiles.map((item) =>
              item.file === fileInfo.file
                ? { ...item, url: uploadedUrl, uploading: false }
                : item
            );
            onFilesChange(currentFiles);
          } catch (error) {
            currentFiles = currentFiles.map((item) =>
              item.file === fileInfo.file
                ? { ...item, uploading: false, error: true }
                : item
            );
            onFilesChange(currentFiles);
          }
        }
      }
    }
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {files.length === 0 ? (
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition-colors cursor-pointer"
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple
            className="hidden"
            onChange={handleChange}
          />
          <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2 block"></i>
          <p className="text-sm text-gray-600 font-medium">
            Choose a file or drag &amp; drop it here.
          </p>
          <button
            type="button"
            className="mt-3 px-4 py-1.5 border border-gray-300 rounded-md text-sm text-gray-600 bg-white hover:bg-gray-50 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            Browse File
          </button>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple
            className="hidden"
            onChange={handleChange}
          />
          <div className="space-y-2">
            {files.map((fileInfo, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-3 py-3 border border-gray-200 rounded-xl bg-white"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileIcon filename={fileInfo.file.name} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate max-w-[160px]">
                      {fileInfo.file.name}
                    </p>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                      {fileInfo.uploading ? (
                        <>
                          <div className="animate-spin h-3 w-3 border-b-2 border-purple-600 rounded-full" />
                          <span>Uploading...</span>
                        </>
                      ) : fileInfo.error ? (
                        <>
                          <i className="ri-error-warning-fill text-red-500 text-[14px]"></i> 
                          <span className="text-red-500 font-medium">Failed to upload</span>
                        </>
                      ) : (
                        <>
                          <span>
                            {formatFileSize(fileInfo.file.size)} of {formatFileSize(fileInfo.file.size)}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1 font-medium text-gray-900">
                            <i className="ri-checkbox-circle-fill text-green-500 text-[14px]"></i> Completed
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {!fileInfo.uploading && (
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="text-gray-400 hover:text-red-500 transition-colors ml-2 flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const ReportIncidentModal: React.FC<ReportIncidentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [handovers, setHandovers] = useState<Handover[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const defaultForm = (): IncidentFormData => ({
    handoverID: '',
    vehiclePlatNomor: '',
    issueType: '',
    incidentDate: '',
    severity: '' as IssueSeverity,
    chronology: '',
    costBearer: '',
    mitraName: '',
    clientName: '',
    vehicleOemName: '',
    photos: [],
    policeReports: [],
    notes: '',
  });

  const [formData, setFormData] = useState<IncidentFormData>(defaultForm());

  const loadData = useCallback(async () => {
    setIsLoadingData(true);
    try {
      const response = await getListHandover({
        page: '1',
        size: '999',
        isActive: true,
        isAvailable: 'true',
        statuses: 'ACTIVE',
      });
      setHandovers(response.data || []);
    } catch (error) {
      console.error('Error loading handovers:', error);
      setHandovers([]);
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadData();
      setFormData(defaultForm());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handoverOptions = useMemo(
    () =>
      handovers
        .filter((h) => h.ID)
        .map((h) => ({
          value: h.ID,
          label: `${h.platNomor} - ${h.oemName} - ${h.modelName}`,
          data: h,
        })),
    [handovers]
  );

  const selectedHandover = useMemo(
    () => handovers.find((h) => h.ID === formData.handoverID),
    [handovers, formData.handoverID]
  );

  useEffect(() => {
    if (selectedHandover) {
      setFormData((prev) => ({
        ...prev,
        mitraName: `${selectedHandover.driverCode || ''} - ${selectedHandover.driverName || 'No Name'}`,
        clientName: selectedHandover.clientName || selectedHandover.client || '',
        vehicleOemName: selectedHandover.oemName || '',
        oemID: selectedHandover.oemID || '',
        driverID: selectedHandover.driverID || undefined,
        driverCode: selectedHandover.driverCode || undefined,
        driverName: selectedHandover.driverName || undefined,
        clientID: Number(selectedHandover.clientID) || 0,
        costBearer: '',
      }));
    }
  }, [selectedHandover]);

  // Same filtered cost bearer options as ReportIssueModal
  const availableCostBearerOptions = useMemo(() => {
    const allOptions = [
      { value: 'MITRA', label: 'Mitra' },
      { value: 'CLIENT', label: 'Client' },
      { value: 'OEM', label: 'OEM' },
      { value: 'DASH_ELECTRIC', label: 'Dash' },
    ];

    if (!selectedHandover) return allOptions;

    return allOptions.filter((option) => {
      if (option.value === 'DASH_ELECTRIC') return true;
      if (option.value === 'MITRA') {
        return !!(selectedHandover.driverName || selectedHandover.driverCode);
      }
      if (option.value === 'CLIENT') {
        return !!(selectedHandover.clientName || selectedHandover.client);
      }
      if (option.value === 'OEM') {
        return !!(selectedHandover.oemName || selectedHandover.oemID);
      }
      return true;
    });
  }, [selectedHandover]);

  // Auto-clear costBearer if no longer available
  useEffect(() => {
    if (
      formData.costBearer &&
      !availableCostBearerOptions.some((opt) => opt.value === formData.costBearer)
    ) {
      setFormData((prev) => ({ ...prev, costBearer: '' }));
    }
  }, [availableCostBearerOptions, formData.costBearer]);

  // Reset costBearer when handover changes
  useEffect(() => {
    if (formData.handoverID) {
      setFormData((prev) => ({ ...prev, costBearer: '' }));
    }
  }, [formData.handoverID]);

  // Dynamic detail for selected cost bearer
  const costBearerDetail = useMemo(() => {
    if (!formData.costBearer || formData.costBearer === 'DASH_ELECTRIC') return null;

    const details: Record<string, { label: string; value: string | undefined | null }> = {
      MITRA: {
        label: 'Mitra Name',
        value: selectedHandover
          ? `${selectedHandover.driverCode || ''} - ${selectedHandover.driverName || 'No Name'}`
          : null,
      },
      CLIENT: {
        label: 'Client Name',
        value: selectedHandover?.clientName || selectedHandover?.client,
      },
      OEM: {
        label: 'OEM Name',
        value: selectedHandover?.oemName,
      },
    };

    return details[formData.costBearer];
  }, [formData.costBearer, selectedHandover]);

  const isFormValid =
    !!formData.handoverID &&
    !!formData.issueType &&
    !!formData.incidentDate &&
    !!formData.severity &&
    !!formData.chronology.trim() &&
    !!formData.costBearer &&
    formData.photos.length > 0;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let uploadedDamageUrl = '';
      let uploadedPoliceReportUrl = '';

      if (formData.photos.length > 0) {
        uploadedDamageUrl = formData.photos[0].url || '';
        if (!uploadedDamageUrl) {
          throw new Error('Please wait for the image upload to complete.');
        }
      }

      if (formData.policeReports.length > 0) {
        uploadedPoliceReportUrl = formData.policeReports[0].url || '';
      }

      await onSubmit({
        ...formData,
        notes: formData.chronology,
        imageURL: uploadedDamageUrl,
        policeReportURL: uploadedPoliceReportUrl || undefined,
        selectedHandover: selectedHandover,
      });
    } catch (error) {
      console.error(error);
      const msg = error instanceof Error ? error.message : 'Failed to submit incident. Please try again.';
      showErrorToast('Error', msg);
    } finally {
      setIsSaving(false);
    }
  };

  const uploadIncidentImage = async (file: File) => {
    const uuid = uuidv4();
    const bucketRes = await getBucketUrlImageV2({
      uuid,
      type: INCIDENT_IMAGE_TYPE,
    });
    const url = bucketRes.data;
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
    const uploadRes = await uploadFileImage(url, base64.split(',')[1], file.name);
    if (uploadRes.status === 200) {
      showSuccessToast('Success', 'Foto kerusakan kendaraan berhasil diupload');
      return url;
    }
    throw new Error('Image upload failed');
  };

  const uploadIncidentFile = async (file: File) => {
    const uuid = uuidv4();
    const bucketRes = await getBucketUrlFile({
      uuid,
      fileType: INCIDENT_FILE_TYPE,
      fileFormat: file.name.split('.').pop() || 'pdf',
    });
    const url = bucketRes.data;
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
    const uploadRes = await uploadTempFile(url, base64.split(',')[1], file.name, file.type);
    if (uploadRes.status === 200) {
      showSuccessToast('Success', 'Laporan polisi berhasil diupload');
      return url;
    }
    throw new Error('File upload failed');
  };

  if (!isOpen) return null;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      width="sm"
      contentRef={scrollRef}
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Report Incident</h2>
          <p className="text-sm font-normal text-gray-500">New Incident details.</p>
        </div>
      }
      footer={
        <Button
          variant="primary"
          onClick={handleSave}
          className="w-full py-3"
          disabled={!isFormValid || isSaving || isLoading || formData.photos.some(p => p.uploading)}
        >
          {isSaving || isLoading ? 'Saving...' : 'Next'}
        </Button>
      }
    >
      <div className="flex-1">
        <div className="bg-gray-50 px-6 py-2 font-medium uppercase tracking-wider border-b border-gray-200 heading-field" style={{ fontSize: '12px' }}>
          Incident Detail
        </div>

        <div className="p-6 pb-40">
          {/* Plate Number */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plate Number <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={handoverOptions}
              value={formData.handoverID}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, handoverID: val }))
              }
              placeholder={
                isLoadingData ? 'Loading...' : 'e.g. B 1234 ABC - Electrum - H3'
              }
              disabled={isLoadingData}
              className="w-full"
            />
          </div>

          {/* Issue Type */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Issue Type <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={[
                { value: 'ACCIDENT_DAMAGE', label: 'Kecelakaan' },
                { value: 'MINOR_DAMAGE', label: 'Kerusakan Ringan' },
              ]}
              value={formData.issueType}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, issueType: val }))
              }
              placeholder="e.g. Kecelakaan"
              className="w-full"
            />
          </div>

          {/* Tanggal Kejadian */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tanggal Kejadian <span className="text-red-500">*</span>
            </label>
            <DatePicker
              value={formData.incidentDate}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, incidentDate: val }))
              }
              showTimeSelect={true}
              placeholder="e.g. 01/01/2026 15:30"
              className="w-full"
            />
          </div>

          {/* Severity */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Severity <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={severityOptions}
              value={formData.severity}
              onChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  severity: val as IssueSeverity,
                }))
              }
              placeholder="e.g. Medium"
              className="w-full"
            />
          </div>

          {/* Chronology / Description */}
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chronology / Description <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                rows={4}
                maxLength={200}
                placeholder="e.g. Motor dijemput oleh tim Electrum"
                value={formData.chronology}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, chronology: e.target.value }))
                }
              />
              <div className="absolute bottom-3 right-2 text-xs text-gray-400">
                {formData.chronology.length}/200{' '}
                <i className="ri-draggable"></i>
              </div>
            </div>
          </div>

          {/* Foto Kerusakan Kendaraan */}
          <FileUploadField
            label="Foto Kerusakan Kendaraan"
            required
            accept="image/*"
            files={formData.photos}
            onFilesChange={(newFiles) =>
              setFormData((prev) => ({ ...prev, photos: newFiles }))
            }
            uploadFn={uploadIncidentImage}
            className="mb-5"
          />

          {/* Laporan Polisi (PDF) */}
          <FileUploadField
            label="Laporan Polisi (PDF)"
            accept=".pdf,application/pdf"
            files={formData.policeReports}
            onFilesChange={(newFiles) =>
              setFormData((prev) => ({ ...prev, policeReports: newFiles }))
            }
            uploadFn={uploadIncidentFile}
            className="mb-5"
          />

          {/* Biaya Ditanggung */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Biaya Ditanggung <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={availableCostBearerOptions}
              value={formData.costBearer}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, costBearer: val }))
              }
              placeholder="e.g. Mitra"
              className="w-full"
            />
          </div>

          {/* Dynamic detail field (Mitra Name / Client Name / OEM Name) */}
          {costBearerDetail && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {costBearerDetail.label} <span className="text-red-500">*</span>
              </label>
              <div className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-600">
                {costBearerDetail.value || 'Select Plate Number first'}
              </div>
            </div>
          )}
        </div>
      </div>
    </SideBarModal>
  );
};
